/**
 * Turns a plain list of records into the figures a PDF report needs:
 * headline metrics, a readable data table and grouped breakdowns.
 *
 * This is the single place report numbers are derived, so the Analyzer,
 * Dashboards and Predictive Models all describe a dataset the same way.
 */

export type Row = Record<string, unknown>;

export type ReportMetric = { label: string; value: string };
export type ReportTable = {
  title?: string;
  columns: string[];
  rows: (string | number)[][];
  note?: string;
};
export type ReportBreakdown = {
  title: string;
  chart: "bar" | "pie";
  items: { name: string; value: number }[];
};

const MAX_TABLE_ROWS = 25;
const MAX_TABLE_COLUMNS = 8;

const isBlank = (v: unknown) => v === null || v === undefined || v === "";

export const formatNumber = (n: number): string => {
  if (!isFinite(n)) return "—";
  if (Number.isInteger(n)) return n.toLocaleString();
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const cell = (v: unknown): string => {
  if (isBlank(v)) return "";
  if (typeof v === "number") return formatNumber(v);
  if (typeof v === "object") return JSON.stringify(v).slice(0, 60);
  return String(v).slice(0, 60);
};

export const listColumns = (rows: Row[]): string[] => {
  const seen = new Set<string>();
  rows.slice(0, 200).forEach((r) => Object.keys(r || {}).forEach((k) => seen.add(k)));
  return Array.from(seen).filter((k) => !k.startsWith("_"));
};

const numericColumns = (rows: Row[], columns: string[]): string[] =>
  columns.filter((c) => {
    const values = rows.map((r) => r[c]).filter((v) => !isBlank(v));
    if (!values.length) return false;
    return values.every((v) => typeof v === "number" || (typeof v === "string" && v.trim() !== "" && !isNaN(Number(v))));
  });

const categoricalColumns = (rows: Row[], columns: string[]): string[] =>
  columns.filter((c) => {
    const values = rows.map((r) => r[c]).filter((v) => !isBlank(v));
    if (values.length < rows.length * 0.5) return false;
    if (values.some((v) => typeof v === "number")) return false;
    const unique = new Set(values.map((v) => String(v)));
    return unique.size > 1 && unique.size <= 12;
  });

const toNumber = (v: unknown): number => (typeof v === "number" ? v : Number(v));

/** Completeness = share of filled cells across every row and column. */
export const completeness = (rows: Row[], columns: string[]): number => {
  if (!rows.length || !columns.length) return 0;
  let filled = 0;
  rows.forEach((r) => columns.forEach((c) => { if (!isBlank(r[c])) filled += 1; }));
  return filled / (rows.length * columns.length);
};

/** Headline metrics: size, quality and the totals/averages that matter most. */
export const buildMetrics = (rows: Row[], totalRows?: number): ReportMetric[] => {
  const columns = listColumns(rows);
  const metrics: ReportMetric[] = [
    { label: "Total records", value: formatNumber(totalRows ?? rows.length) },
    { label: "Columns", value: formatNumber(columns.length) },
    { label: "Completeness", value: `${Math.round(completeness(rows, columns) * 100)}%` },
  ];

  // Identifier-ish columns are numbers but summing them is meaningless.
  const isIdentifier = (c: string) =>
    /(^|_)(id|ids|code|ref|zip|postcode|phone|year|number|no)$/i.test(c.trim());
  const numeric = numericColumns(rows, columns).filter((c) => !isIdentifier(c)).slice(0, 2);
  numeric.forEach((c) => {
    const values = rows.map((r) => toNumber(r[c])).filter((n) => isFinite(n));
    if (!values.length) return;
    const total = values.reduce((a, b) => a + b, 0);
    metrics.push({ label: `Total ${c.replace(/_/g, " ")}`, value: formatNumber(total) });
  });

  const categorical = categoricalColumns(rows, columns)[0];
  if (categorical && metrics.length < 6) {
    const unique = new Set(rows.map((r) => String(r[categorical] ?? "")).filter(Boolean));
    metrics.push({
      label: `Unique ${categorical.replace(/_/g, " ")}`,
      value: formatNumber(unique.size),
    });
  }

  return metrics.slice(0, 6);
};

/** The data log table — capped so the file stays openable. */
export const buildDataTable = (rows: Row[], totalRows?: number): ReportTable | undefined => {
  if (!rows.length) return undefined;
  const columns = listColumns(rows).slice(0, MAX_TABLE_COLUMNS);
  if (!columns.length) return undefined;
  const shown = rows.slice(0, MAX_TABLE_ROWS);
  const total = totalRows ?? rows.length;

  return {
    title: "Data log",
    columns: columns.map((c) => c.replace(/_/g, " ")),
    rows: shown.map((r) => columns.map((c) => cell(r[c]))),
    note:
      total > shown.length
        ? `Showing the first ${shown.length} of ${formatNumber(total)} records.`
        : undefined,
  };
};

/** Grouped analysis for the columns that actually group well. */
export const buildBreakdowns = (rows: Row[], limit = 2): ReportBreakdown[] => {
  const columns = listColumns(rows);
  const groupCols = categoricalColumns(rows, columns).slice(0, limit);
  const numeric = numericColumns(rows, columns)[0];

  return groupCols.map((col, index) => {
    const totals = new Map<string, number>();
    rows.forEach((r) => {
      const key = isBlank(r[col]) ? "Unknown" : String(r[col]);
      const add = numeric && isFinite(toNumber(r[numeric])) ? toNumber(r[numeric]) : 1;
      totals.set(key, (totals.get(key) ?? 0) + add);
    });

    const items = Array.from(totals, ([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);

    const label = (text: string) => {
      const clean = text.replace(/_/g, " ");
      return clean.charAt(0).toUpperCase() + clean.slice(1);
    };

    return {
      title: numeric
        ? `${label(numeric)} by ${col.replace(/_/g, " ")}`
        : `Records by ${col.replace(/_/g, " ")}`,
      chart: index === 0 && items.length <= 6 ? "pie" : "bar",
      items,
    };
  });
};

/** Condensed metrics table used near the end of the report. */
export const buildSummaryTable = (metrics: ReportMetric[], extra: ReportMetric[] = []): ReportTable => ({
  title: "Overall summary",
  columns: ["Measure", "Value"],
  rows: [...metrics, ...extra].map((m) => [m.label, m.value]),
});

/** Best-effort date range from any date-looking column. */
export const detectDateRange = (rows: Row[]): string | undefined => {
  const columns = listColumns(rows);
  const dateCol = columns.find((c) => /date|time|created|updated|period/i.test(c));
  if (!dateCol) return undefined;
  const stamps = rows
    .map((r) => new Date(String(r[dateCol] ?? "")).getTime())
    .filter((t) => isFinite(t) && t > 0);
  if (stamps.length < 2) return undefined;
  const fmt = (t: number) => new Date(t).toLocaleDateString();
  return `${fmt(Math.min(...stamps))} – ${fmt(Math.max(...stamps))}`;
};
